import { useEffect, useRef, useState } from "react";
import { here, Here } from "../../desktop/desktop";
import DockWidget from "@common/components/DockWidget";
import { useSession } from "@jkast90/kast-common/auth";
import IconButton from "@common/components/IconButton";
import { formatSize } from "@common/format";
import { saveFileLabel, saveFileName } from "../saves";
import { formatPlayTime } from "../saveStats";
import { stateIsFresh } from "../slots";
import { localSavesOwnership, NOT_YOURS } from "../syncRecords";
import { useCloudSync } from "../useCloudSync";
import { AUTO_SAVE_MS, type Emulator } from "../useEmulator";
import { CloudLibrary } from "./CloudLibrary";
import { useConfirm } from "@common/components/ConfirmDialog";
import { signIn } from "../../auth/session";

const formatTime = (time: string | Date | number) => new Date(time).toLocaleString();

const describeSlot = (stats: { at: number; playTime: { hours: number; minutes: number } | null } | undefined) =>
  stats ? [stats.playTime && formatPlayTime(stats.playTime), formatTime(stats.at)].filter(Boolean).join(" · ") : null;

export default function SavePanel({ emu, dock }: { emu: Emulator; dock: HTMLElement | null }) {
  const { confirm, dialog: confirmDialog } = useConfirm();
  const isAuthenticated = useSession().status === "signedIn";
  const slotInput = useRef<HTMLInputElement>(null);
  const slotForImport = useRef<number>(1);
  const [cloudMsg, setCloudMsg] = useState<string | null>(null);
  const [showCloudLibrary, setShowCloudLibrary] = useState(false);
  const running = !!emu.current;
  // N64 runs on EmulatorJS: slots work the same, but only the cartridge
  // save syncs, and there is no .sav to export or import.
  const ejs = emu.engine === "ejs";
  const sync = useCloudSync(emu, isAuthenticated, (q) => confirm({ title: q.title, message: q.message, action: q.action, danger: false }));
  const [, tick] = useState(0);

  // Freshness is time-based, so re-render on a slow clock while playing.
  useEffect(() => {
    if (!running) return;
    const timer = window.setInterval(() => tick((n) => n + 1), 5000);
    return () => window.clearInterval(timer);
  }, [running]);

  const savedTitle = (slot: number) => {
    const at = emu.stateSavedAt(slot);
    return at ? `Last saved ${at.toLocaleString()}` : "No state saved";
  };

  const saveDot = (slot: number) => {
    const at = emu.stateSavedAt(slot);
    const title = savedTitle(slot);
    if (!at) return <span className="save-dot none" title={title} aria-label={title} />;
    const fresh = stateIsFresh(at, emu.lastInputAt(), emu.idleSince, AUTO_SAVE_MS);
    return <span className={fresh ? "save-dot fresh" : "save-dot stale"} title={title} aria-label={title} />;
  };

  // A message is about the game and account it was shown for.
  const msgFor = `${isAuthenticated}:${emu.current}`;
  const [shownFor, setShownFor] = useState(msgFor);
  if (shownFor !== msgFor) {
    setShownFor(msgFor);
    setCloudMsg(null);
  }

  const cloudList = sync.cloud;
  const busy = sync.busy;
  const signInNeeded = sync.expired;

  const runCloud = async (work: (rom: string) => Promise<string>) => {
    const rom = emu.current;
    if (!rom) return;
    if (localSavesOwnership() === "other") {
      setCloudMsg(NOT_YOURS(Here));
      return;
    }
    try {
      setCloudMsg(await work(rom));
    } catch (e) {
      setCloudMsg(e instanceof Error ? e.message : "Cloud save failed.");
    }
  };

  const uploadToCloud = () =>
    runCloud(async (rom) => {
      const local = emu.localSaves();
      if (!local.some((s) => s.name === saveFileName(rom))) return "No battery save yet. Save in-game first.";
      if (
        sync.newer &&
        !(await confirm({
          title: "Overwrite the cloud's save?",
          message: `The cloud has a newer save from another device (${formatTime(sync.newer.at)}).`,
          action: "Overwrite",
        }))
      ) {
        return "Upload cancelled.";
      }
      await sync.push();
      return `Uploaded ${local.map((s) => saveFileLabel(s.name).toLowerCase()).join(", ")}.`;
    });

  const restoreFromCloud = () =>
    runCloud(async () => {
      if (!cloudList?.length) return "Nothing in the cloud for this game.";
      if (
        sync.dirty &&
        !(await confirm({
          title: "Replace your saves here?",
          message: "Your saves here have changes the cloud doesn't. They are replaced with the cloud's.",
          action: "Replace",
        }))
      ) {
        return "Restore cancelled.";
      }
      await sync.pull();
      return "Cloud save restored.";
    });

  const syncStatus = () => {
    if (sync.error) return `Auto-sync paused: ${sync.error}`;
    if (sync.newer?.conflict) {
      return `This game was also saved on another device (${formatTime(sync.newer.at)}). Upload to keep ${here}'s progress, or restore to take the cloud's.`;
    }
    if (sync.newer) return `A newer save from another device is in the cloud (${formatTime(sync.newer.at)}). Restore to load it; the game restarts.`;
    if (sync.syncedAt) return `Auto-synced · last matched the cloud ${sync.syncedAt.toLocaleTimeString()}`;
    return null;
  };

  const content = (
    <>
      <p className="muted small">
        {!ejs
          ? `In-game saves persist in ${here} automatically. Export a copy, or import one into a slot below.`
          : `The cartridge save syncs with the cloud. States stay in ${here}: they are too big for cloud saves.`}
      </p>

      <div className="section-head">
        <h3>Save states</h3>
        <div className="btn-group">
          <IconButton icon="add" label="Add slot" disabled={!running} onClick={emu.addSlot} />
        </div>
      </div>
      {(() => {
        const stats = running ? emu.saveStats() : null;
        if (!stats) return null;
        return (
          <p className="muted small">
            {[
              stats.trainer && `Trainer ${stats.trainer}`,
              stats.playTime && `${formatPlayTime(stats.playTime)} played`,
              stats.dexOwned !== null && `${stats.dexOwned} caught`,
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        );
      })()}
      <div className="slots">
        {emu.slots.map((slot) => (
          <div key={slot} className={slot === emu.activeSlot ? "slot active" : "slot"} title={savedTitle(slot)}>
            {saveDot(slot)}
            <div className="slot-main">
              <input
                className="slot-name"
              type="text"
              maxLength={40}
              placeholder={`Slot ${slot}`}
              value={emu.slotNames[slot] ?? ""}
              disabled={!running}
              aria-label={`Name for slot ${slot}`}
              onChange={(e) => emu.renameSlot(slot, e.target.value)}
              onFocus={() => emu.captureKeyboard(false)}
              onBlur={() => emu.captureKeyboard(true)}
              />
              {describeSlot(emu.slotStats[slot]) && (
                <span className="muted small slot-meta">{describeSlot(emu.slotStats[slot])}</span>
              )}
            </div>
            <IconButton icon="save" label="Save state" disabled={!running} onClick={() => emu.saveState(slot)} />
            <IconButton icon="folder_open" label="Load state" disabled={!running} onClick={() => emu.loadState(slot)} />
            {!ejs && slot === emu.activeSlot && (
              <IconButton icon="download" label="Export the battery save (.sav)" disabled={!running} onClick={emu.exportSave} />
            )}
            {!ejs && (
              <IconButton
                icon="file_upload"
                label="Import a .sav into this slot"
                disabled={!running}
                onClick={() => {
                  slotForImport.current = slot;
                  slotInput.current?.click();
                }}
              />
            )}
            <IconButton
              icon="delete"
              label="Remove slot"
              className="danger"
              disabled={!running}
              onClick={() => {
                const where = isAuthenticated && !ejs ? " here and in the cloud" : "";
                confirm({ title: `Delete ${emu.slotLabel(slot)}?`, message: `Its saved state is deleted${where}. This can't be undone.` }).then(
                  (yes) => {
                    if (yes) emu.removeSlot(slot);
                  },
                );
              }}
            />
          </div>
        ))}
        <input
          ref={slotInput}
          type="file"
          accept=".sav"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) emu.importSaveToSlot(slotForImport.current, file);
            e.target.value = "";
          }}
        />
      </div>
      <label className="check">
        <input
          type="checkbox"
          checked={emu.autoSave}
          disabled={!running || !emu.activeSlot}
          onChange={(e) => emu.setAutoSave(e.target.checked)}
        />
        <span>
          Auto-save
          {emu.activeSlot
            ? ` to ${emu.slotLabel(emu.activeSlot)} every ${AUTO_SAVE_MS / 1000} s while you play`
            : " (save or load a slot first)"}
          {emu.autoSave && emu.lastAutoSave && (
            <span className="muted"> · saved {emu.lastAutoSave.toLocaleTimeString()}</span>
          )}
        </span>
      </label>

      <div className="section-head">
        <h3>Cloud</h3>
        {isAuthenticated && (
          <div className="btn-group">
            <IconButton icon="cloud_download" label="Download a ROM from your cloud library" onClick={() => setShowCloudLibrary(true)} />
          </div>
        )}
      </div>
      {isAuthenticated ? (
        <>
          {!running && <p className="muted small">Start a game to see its cloud saves.</p>}
          {running && (
            <div className="section-head sub">
              <span className="muted small">This game</span>
              <div className="btn-group">
                <IconButton icon="cloud_upload" label="Upload this game's saves" disabled={busy} onClick={uploadToCloud} />
                <IconButton
                  icon="history"
                  label="Restore this game's saves"
                  disabled={busy || !cloudList?.length}
                  onClick={restoreFromCloud}
                />
              </div>
            </div>
          )}
          {running && cloudList === null && !cloudMsg && !sync.error && <p className="muted small">Checking the cloud…</p>}
          {cloudList && cloudList.length === 0 && <p className="muted small">Nothing in the cloud for this game yet.</p>}
          {cloudList && cloudList.length > 0 && (
            <ul className="cloud-list">
              {cloudList.map((s) => (
                <li key={s.name}>
                  <span>{saveFileLabel(s.name)}</span>
                  <span className="muted small">
                    {formatTime(s.updatedAt)} · {formatSize(s.size)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {running && syncStatus() && (
            <div className="row">
              <p className="muted small" style={{ flex: 1, margin: 0 }}>
                {syncStatus()}
              </p>
              {signInNeeded && (
                <IconButton icon="login" label="Sign in again" className="primary" onClick={signIn} />
              )}
            </div>
          )}
          {cloudMsg && <p className="muted small">{cloudMsg}</p>}
          {showCloudLibrary && <CloudLibrary emu={emu} onClose={() => setShowCloudLibrary(false)} />}
        </>
      ) : (
        <div className="row">
          <p className="muted small" style={{ flex: 1, margin: 0 }}>
            Sign in with your kast-dev account to keep saves and ROMs across devices.
          </p>
          <IconButton icon="login" label="Sign in" onClick={signIn} />
        </div>
      )}
    </>
  );

  // The dialog renders whether or not a game runs: cloud sync waits on its
  // answer, and while it was missing here every upload queued behind a
  // question nobody could see.
  if (running) {
    return (
      <>
      {confirmDialog}
      <DockWidget
        key={emu.current}
        dock={dock}
        label="Saves"
        summary={
          emu.activeSlot ? (
            <span className="name slot-title" title={savedTitle(emu.activeSlot)}>
              {saveDot(emu.activeSlot)}
              {emu.slotLabel(emu.activeSlot)}
              {emu.slotStats[emu.activeSlot]?.playTime && (
                <span className="muted small">{formatPlayTime(emu.slotStats[emu.activeSlot]!.playTime!)}</span>
              )}
            </span>
          ) : (
            <span className="muted small">No slot yet</span>
          )
        }
      >
        {content}
      </DockWidget>
      </>
    );
  }

  return (
    <section className="panel">
      <h2>Saves</h2>
      {content}
      {confirmDialog}
    </section>
  );
}
